import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubdomain } from '../contexts/SiteContext';
import { buildApiUrl, API_ENDPOINTS, authenticatedFetch } from '../config/api';
import moduleService from '../services/moduleService';
import './SystemLanding.css';

const SystemLanding = () => {
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const { subdomains, loadSubdomains } = useSubdomain();
  const [currentFeature, setCurrentFeature] = useState(0);
  const [totalMembersCount, setTotalMembersCount] = useState(0);
  const [subdomainsLoaded, setSubdomainsLoaded] = useState(false);

  // Obtener definiciones de planes
  const planDefinitions = moduleService.getPlanDefinitions();

  // Cargar subdomains solo para managers y solo una vez
  useEffect(() => {
    if (user && hasRole('manager') && !subdomainsLoaded) {
      setSubdomainsLoaded(true);
      loadSubdomains(false).catch(error => {
        console.warn('Error loading subdomains:', error);
        // No bloquear la UI si falla la carga de subdomains
      });
    }
  }, [user, hasRole, loadSubdomains, subdomainsLoaded]);

  const systemFeatures = [
    {
      icon: '🏢',
      title: 'Gestión Multi-Centro',
      description: 'Administra múltiples academias deportivas desde una sola plataforma centralizada.',
      highlights: ['Múltiples ubicaciones', 'Gestión unificada', 'Reportes consolidados']
    },
    {
      icon: '👥',
      title: 'Gestión de Miembros',
      description: 'Controla membresías, pagos, asistencia y seguimiento de progreso de tus estudiantes.',
      highlights: ['Perfiles detallados', 'Control de pagos', 'Seguimiento personalizado']
    },
    {
      icon: '📅',
      title: 'Horarios Inteligentes',
      description: 'Organiza clases y entrenamientos con calendario visual y reservas automáticas.',
      highlights: ['Calendario intuitivo', 'Reservas automáticas', 'Notificaciones']
    },
    {
      icon: '💰',
      title: 'Control Financiero',
      description: 'Gestiona ingresos, egresos, membresías y genera reportes financieros automáticos.',
      highlights: ['Control de ingresos', 'Reportes automáticos', 'Gestión de morosidad']
    },
    {
      icon: '📊',
      title: 'Analytics y Reportes',
      description: 'Obtén insights valiosos sobre el crecimiento y rendimiento de tu academia.',
      highlights: ['Estadísticas en tiempo real', 'Reportes personalizados', 'Tendencias']
    },
    {
      icon: '📱',
      title: 'Acceso Móvil',
      description: 'Accede desde cualquier dispositivo con interfaz responsive y sincronización en tiempo real.',
      highlights: ['Acceso universal', 'Interfaz responsive', 'Sincronización']
    }
  ];


  // Calcular sucursales (sites que tienen parent_site_id)
  const branchSites = subdomains.filter(site => site.parent_site_id);
  
  // Debug: Log de sites y sucursales
  console.log('🏢 Sites cargados:', subdomains.map(s => ({ 
    id: s.id, 
    name: s.name, 
    parent_site_id: s.parent_site_id,
    locationsCount: s.locations?.length || 0
  })));
  console.log('🏪 Sucursales encontradas:', branchSites.length, branchSites.map(s => ({ id: s.id, name: s.name })));
  
  // Calcular total de ubicaciones de todos los sites
  const totalLocations = subdomains.reduce((total, site) => {
    return total + (site.locations?.length || 0);
  }, 0);
  
  // Calcular total de horarios de todas las ubicaciones
  const totalSchedules = subdomains.reduce((total, site) => {
    if (site.locations && Array.isArray(site.locations)) {
      return total + site.locations.reduce((siteTotal, location) => {
        if (location.practice_schedules && Array.isArray(location.practice_schedules)) {
          return siteTotal + location.practice_schedules.length;
        }
        return siteTotal;
      }, 0);
    }
    return total;
  }, 0);
  
  console.log('📍 Total de ubicaciones:', totalLocations);
  console.log('📅 Total de horarios:', totalSchedules);
  
  const stats = [
    {
      number: subdomains.length,
      label: 'Centros Deportivos',
      icon: '🏢'
    },
    {
      number: totalLocations,
      label: 'Ubicaciones',
      icon: '📍'
    },
    {
      number: totalMembersCount,
      label: 'Miembros Activos',
      icon: '👥'
    },
    {
      number: totalSchedules,
      label: 'Horarios Programados',
      icon: '📅'
    }
  ];

  // Cargar conteo total de miembros cuando cambien los subdomains
  useEffect(() => {
    const loadTotalMembersCount = async () => {
      if (subdomains.length === 0) {
        setTotalMembersCount(0);
        return;
      }

      try {
        let totalCount = 0;
        for (const site of subdomains) {
          try {
            const apiUrl = buildApiUrl(API_ENDPOINTS.SITE_MEMBERS(site.id));
            const response = await authenticatedFetch(apiUrl);
            if (response.ok) {
              const members = await response.json();
              totalCount += members.length;
            }
          } catch (error) {
            console.warn(`Error loading members for site ${site.id}:`, error);
          }
        }
        setTotalMembersCount(totalCount);
      } catch (error) {
        console.warn('Error loading total members count:', error);
        setTotalMembersCount(0);
      }
    };

    if (subdomainsLoaded) {
      loadTotalMembersCount();
    }
  }, [subdomains, subdomainsLoaded]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % systemFeatures.length);
    }, 4000);

    return () => clearInterval(timer);
  }, [systemFeatures.length]);

  const getGreeting = () => {
    if (!user) return 'Bienvenido al Sistema de Gestión Deportiva';
    
    const hour = new Date().getHours();
    let timeGreeting = '';
    if (hour < 12) timeGreeting = 'Buenos días';
    else if (hour < 18) timeGreeting = 'Buenas tardes';
    else timeGreeting = 'Buenas noches';
    
    return `${timeGreeting}, ${user.name}`;
  };

  const getWelcomeMessage = () => {
    if (!user) {
      return 'La plataforma completa para administrar tu academia deportiva. Gestiona horarios, miembros, pagos y mucho más desde un solo lugar.';
    }
    
    if (hasRole('manager')) {
      if (subdomains.length === 0) {
        return 'Es hora de crear tu primer centro deportivo y comenzar a gestionar tu academia de manera profesional.';
      } else {
        return `Tienes ${subdomains.length} centro${subdomains.length > 1 ? 's' : ''} deportivo${subdomains.length > 1 ? 's' : ''} activo${subdomains.length > 1 ? 's' : ''}. Gestiona tus academias, miembros y actividades.`;
      }
    }
    
    if (hasRole('member')) {
      return 'Bienvenido a tu área personal. Aquí puedes ver tu información, clases y actividades programadas.';
    }
    
    return 'Bienvenido al sistema. Explora las funcionalidades disponibles.';
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const handleCreateCenter = () => {
    navigate('/manager');
  };

  return (
    <div className="system-landing">
      {/* Hero Section */}
      <div className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              {getGreeting()}
            </h1>
            <p className="hero-subtitle">
              {getWelcomeMessage()}
            </p>
          </div>
          
          <div className="hero-actions">
            {!user ? (
              <button 
                onClick={handleLogin}
                className="cta-button primary"
              >
                <span className="btn-icon">🚀</span>
                Comenzar Ahora
              </button>
            ) : hasRole('manager') && subdomains.length === 0 ? (
              <button 
                onClick={handleCreateCenter}
                className="cta-button primary"
              >
                <span className="btn-icon">🏢</span>
                Crear Mi Primer Centro
              </button>
            ) : (
              <div className="user-actions">
                {hasRole('manager') && (
                  <button 
                    onClick={() => navigate('/manager')}
                    className="cta-button primary"
                  >
                    <span className="btn-icon">🏢</span>
                    Dashboard
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>


      {/* Stats Section */}
      {user && hasRole('manager') && (
        <div className="stats-section">
          <div className="container">
            <h2 className="section-title">Resumen de tu Academia</h2>
            <div className="stats-grid">
              {stats.map((stat, index) => (
                <div key={index} className="stat-card">
                  <div className="stat-icon">{stat.icon}</div>
                  <div className="stat-number">{stat.number}</div>
                  <div className="stat-label">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Features Section */}
      <div className="features-section">
        <div className="container">
          <h2 className="section-title">Funcionalidades del Sistema</h2>
          <p className="section-subtitle">
            Descubre todas las herramientas disponibles para optimizar la gestión de tu academia deportiva
          </p>
          
          <div className="features-carousel">
            <div className="feature-display">
              <div className="feature-card-large">
                <div className="feature-icon-large">
                  {systemFeatures[currentFeature].icon}
                </div>
                <h3 className="feature-title-large">
                  {systemFeatures[currentFeature].title}
                </h3>
                <p className="feature-description-large">
                  {systemFeatures[currentFeature].description}
                </p>
                <ul className="feature-highlights">
                  {systemFeatures[currentFeature].highlights.map((highlight, index) => (
                    <li key={index} className="highlight-item">
                      <span className="highlight-icon">✓</span>
                      {highlight}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            
            <div className="feature-navigation">
              <div className="feature-dots">
                {systemFeatures.map((_, index) => (
                  <button
                    key={index}
                    className={`feature-dot ${index === currentFeature ? 'active' : ''}`}
                    onClick={() => setCurrentFeature(index)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Plans Section */}
      {!user && (
        <div className="pricing-section">
          <div className="container">
            <h2 className="section-title">Planes de Suscripción</h2>
            <p className="section-subtitle">
              Elige el plan que mejor se adapte a las necesidades de tu academia deportiva
            </p>
            
            <div className="pricing-grid">
              {Object.entries(planDefinitions).map(([planId, plan]) => (
                <div key={planId} className={`pricing-card ${planId.toLowerCase()}`}>
                  <div className="pricing-header">
                    <h3 className="plan-name">{plan.name}</h3>
                    <div className="plan-price">
                      {plan.price === 0 ? (
                        <span className="price-free">Gratis</span>
                      ) : (
                        <span className="price-amount">${plan.price}<span className="price-period">/mes</span></span>
                      )}
                    </div>
                  </div>
                  
                  <div className="plan-features">
                    <ul>
                      {plan.features.map(moduleId => {
                        const moduleDef = moduleService.getModuleDefinitions()[moduleId];
                        return (
                          <li key={moduleId}>
                            <span className="feature-icon">✓</span>
                            <span className="feature-text">{moduleDef?.name || moduleId}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                  
                  <div className="plan-cta">
                    <button 
                      onClick={handleLogin}
                      className="cta-button primary"
                    >
                      <span className="btn-icon">🚀</span>
                      Comenzar Ahora
                    </button>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="pricing-note">
              <p>
                <strong>Nota:</strong> Los precios están en USD. 
                El plan Intermedio se cobra por ubicación ($10/mes por cada ubicación).
              </p>
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <div className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">¿Necesitas ayuda?</h2>
            <p className="cta-subtitle">
              Nuestro equipo de soporte está disponible para ayudarte a aprovechar al máximo el sistema.
            </p>
            <div className="cta-actions">
              <button 
                onClick={() => navigate('/manager')}
                className="cta-button secondary"
              >
                <span className="btn-icon">📚</span>
                Centro de Ayuda
              </button>
              <button 
                onClick={() => navigate('/manager')}
                className="cta-button primary"
              >
                <span className="btn-icon">💬</span>
                Contactar Soporte
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemLanding;
